import { Alert, Badge, Button } from 'react-bootstrap';

const MAX_SHOWN = 5;

/**
 * Shown under the "new material name" field: lists existing items (Global Material Master + the org's
 * own) whose names match what's being typed, with a "Use this" action, so the user reuses an
 * existing item instead of hitting the duplicate-name error or creating a near-duplicate.
 * `items` is whatever list the parent already has loaded - matching is purely client-side.
 */
function SimilarItemsHint({ name, items, isUsed, onUse }) {
  const query = (name || '').trim().toLowerCase();
  if (query.length < 2) return null;

  const matches = items
    .filter((item) => {
      const itemName = (item.name || '').toLowerCase();
      return itemName.includes(query) || (itemName.length >= 3 && query.includes(itemName));
    })
    .sort((a, b) => {
      const aExact = a.name.toLowerCase() === query ? 0 : 1;
      const bExact = b.name.toLowerCase() === query ? 0 : 1;
      return aExact - bExact || a.name.localeCompare(b.name);
    });

  if (matches.length === 0) return null;

  const hasExact = matches.some((item) => item.name.toLowerCase() === query);

  return (
    <Alert variant={hasExact ? 'warning' : 'info'} className="mt-2 mb-0 py-2 small">
      <div className="mb-1">
        {hasExact
          ? 'An item with this name already exists - use it instead of creating a new one:'
          : 'Similar items already exist - use one of these instead?'}
      </div>
      {matches.slice(0, MAX_SHOWN).map((item) => {
        const used = isUsed ? isUsed(item) : false;
        return (
          <div key={item.id} className="d-flex align-items-center justify-content-between gap-2 py-1">
            <div>
              <strong>{item.name}</strong>
              {item.itemType && <span className="text-muted"> ({item.itemType.name})</span>}{' '}
              <Badge bg={item.createdByOrganization ? 'secondary' : 'primary'}>
                {item.createdByOrganization ? 'Your item' : 'Global Material Master'}
              </Badge>
            </div>
            {used ? (
              <span className="text-muted">Already added</span>
            ) : (
              <Button size="sm" variant="outline-primary" onClick={() => onUse(item)}>
                Use this
              </Button>
            )}
          </div>
        );
      })}
      {matches.length > MAX_SHOWN && (
        <div className="text-muted">…and {matches.length - MAX_SHOWN} more - keep typing to narrow</div>
      )}
    </Alert>
  );
}

export default SimilarItemsHint;
